import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Palette, Fingerprint,
  Check, X, Sun, Moon, RotateCcw,
  Sparkles, Loader2,
  ChevronLeft, Settings, Trophy, BarChart2, TrendingUp, BrainCircuit,
  Users, MessageCircle, Library, Bookmark, Globe, BookOpen, Eye
} from 'lucide-react';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { socialService } from '../services/socialService';
import { useData } from '../contexts/DataContext';
import AvatarRenderer, { AvatarConfig } from '../components/AvatarRenderer';
import AvatarCreatorModal from '../components/AvatarCreatorModal';
import PostDetailModal from '../components/PostDetailModal';
import BookDetailsModal from './BookDetailsModal';
import { dbManager } from '../services/db_manager';
import { extractPostStyle } from '../utils/postUtils';
import { AppBackButton } from '../components/AppBackButton';
import { PageTourButton } from '../components/PageTour';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { motion } from 'framer-motion';
import { getProfileCache, setProfileCache, buildProfileMeta, fetchProfileContent, prefetchOwnProfile } from '../services/profilePrefetch';

const ISO_FLAG_MAP: Record<string, string> = {
  'af': '🇿🇦', 'ar': '🇸🇦', 'arp': '🏹', 'bg': '🇧🇬', 'bo': '☸️', 'br': '⚓',
  'ca': '🏔️', 'ceb': '🇵🇭', 'cs': '🇨🇿', 'cy': '🏴󠁧󠁢󠁷󠁬ês󠁿', 'da': '🇩🇰', 'de': '🇩🇪',
  'el': '🇬🇷', 'en': '🇺🇸', 'enm': '📜', 'eo': '🟢', 'es': '🇪🇸', 'et': '🇪🇪',
  'fa': '🇮🇷', 'fi': '🇫🇮', 'fr': '🇫🇷', 'fur': '⛰️', 'fy': '🇳🇱', 'ga': '🇮🇪',
  'gl': '🌾', 'gla': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'he': '🇮🇱', 'hu': '🇭🇺', 'ia': '🌐', 'ilo': '🇵🇭',
  'is': '🇮🇸', 'it': '🇮🇹', 'iu': '❄️', 'ja': '🇯🇵', 'la': '🏛️', 'mi': '🌀',
  'myn': '🗿', 'na': '🇳🇷', 'nah': '🌋', 'nap': '🍕', 'nl': '🇳🇱', 'no': '🇳🇴',
  'oc': '🌿', 'oj': '🌲', 'pl': '🇵🇱', 'pt': '🇧🇷', 'rmq': '💃', 'ro': '🇷🇴',
  'ru': '🇷🇺', 'sa': '🕉️', 'sco': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'sl': '🇸🇮', 'sr': '🇷🇸', 'sv': '🇸🇪',
  'te': '🇮🇳', 'tl': '🇵🇭', 'yi': '🕎', 'zh': '🇨🇳', 'und': '🌐'
};

const R2_COVER_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/capas-atualizadas/";
const R2_HTML_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-online-htmls/";

const ImportSuccessModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onReadNow?: () => void;
  message: string;
  isDuplicate?: boolean;
}> = ({ isOpen, onClose, onReadNow, message, isDuplicate }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 w-screen h-screen z-[1000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300" style={{ left: 0, top: 0 }}>
      <div className="bg-surface dark:bg-slate-900/90 p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-full max-w-sm text-center space-y-6">
        <div className={`w-20 h-20 ${isDuplicate ? 'bg-warn/10 text-warn' : 'bg-ok/10 text-ok'} rounded-[24px] mx-auto flex items-center justify-center`}>
          {isDuplicate ? <Bookmark size={40} /> : <Check size={40} className="animate-in zoom-in-50 duration-500" />}
        </div>
        <div>
          <h3 className="text-2xl font-medium dark:text-ink tracking-[-0.03em]">{isDuplicate ? "Ops!" : t('common.success')}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 font-medium">{message}</p>
        </div>
        <div className="flex flex-col gap-3">
          {onReadNow && !isDuplicate && (
            <button onClick={onReadNow} className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all">{t('profile.readNow')}</button>
          )}
          <button onClick={onClose} className="text-[13px] font-medium w-full h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full hover:bg-tint-stone transition-all">{t('common.confirm')}</button>
        </div>
      </div>
    </div>
  );
};

const BookCover: React.FC<{ book: any }> = ({ book }) => {
  const [hasError, setHasError] = useState(false);
  const [retryWithR2, setRetryWithR2] = useState(false);
  const { isDarkMode } = useTheme();

  const id = book.id || book.custom_id || "";
  let currentCoverUrl = "";
  
  if (!retryWithR2 && book.cover_url && !book.cover_url.startsWith("generated://")) {
    currentCoverUrl = book.cover_url;
  } else if (id) {
    currentCoverUrl = `${R2_COVER_BASE}${id}.jpg`;
  }

  if (currentCoverUrl && !hasError) {
    return (
      <img
        src={currentCoverUrl}
        alt={book.title}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        onError={() => {
          if (!retryWithR2 && book.cover_url && !book.cover_url.startsWith("generated://")) {
            setRetryWithR2(true);
          } else {
            setHasError(true);
          }
        }}
      />
    );
  }

  return (
    <div className={`w-full h-full flex flex-col items-center justify-center p-4 text-center ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
      <Globe size={48} className="text-slate-400 mb-4 opacity-20" />
      <h4 className="text-[10px] font-normal text-slate-500 dark:text-slate-400 uppercase font-mono tracking-[0.14em] line-clamp-3 px-2 leading-tight">
        {book.title}
      </h4>
    </div>
  );
};

const SuccessModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  message: string;
}> = ({ isOpen, onClose, message }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 w-screen h-screen z-[1000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300" style={{ left: 0, top: 0 }}>
      <div className="bg-surface dark:bg-slate-900/90 p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-full max-w-sm text-center space-y-6">
        <div className="w-20 h-20 bg-ok/10 rounded-[24px] mx-auto flex items-center justify-center text-ok"><Check size={40} className="animate-in zoom-in-50 duration-500" /></div>
        <div><h3 className="text-2xl font-medium dark:text-ink tracking-[-0.03em]">{t('common.success')}</h3><p className="text-sm text-slate-500 dark:text-slate-400 mt-2 font-medium">{message}</p></div>
        <button onClick={onClose} className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all">{t('common.confirm')}</button>
      </div>
    </div>
  );
};

const ColorPickerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  currentColor: string;
  onApply: (color: string) => void;
}> = ({ isOpen, onClose, currentColor, onApply }) => {
  const { t } = useTranslation();
  const [tempColor, setTempColor] = useState(currentColor);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 w-screen h-screen z-[100] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm" style={{ left: 0, top: 0 }}>
      <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-full max-w-sm animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('profile.themeColor')}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"><X size={20} /></button>
        </div>
        <div className="space-y-6">
          <div className="flex flex-col items-center gap-6">
            <div className="w-32 h-32 rounded-[28px] border-4 border-white dark:border-white/[0.06] flex items-center justify-center" style={{ backgroundColor: tempColor }}><Sparkles size={40} className="text-white/40" /></div>
            <input type="color" value={tempColor} onChange={(e) => setTempColor(e.target.value)} className="w-full h-14 cursor-pointer bg-transparent border-none rounded-2xl" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-normal uppercase font-mono text-slate-400 tracking-[0.14em] px-2">{t('profile.settings.hexCode')}</label>
            <input type="text" value={tempColor} onChange={(e) => setTempColor(e.target.value)} className="w-full bg-slate-100 dark:bg-slate-800 px-6 py-4 rounded-2xl font-mono font-bold text-center border-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => onApply('#9AA6B8')} className="text-[13px] font-medium col-span-2 flex items-center justify-center gap-2 h-12 text-slate-500 hover:text-primary transition-colors bg-slate-50 dark:bg-slate-800/50 rounded-full"><RotateCcw size={14} /> {t('common.resetDefault')}</button>
            <button onClick={onClose} className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full">{t('common.cancel')}</button>
            <button onClick={() => onApply(tempColor)} className="text-[13px] font-medium h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all">{t('common.apply')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helpers para cache instantâneo SWR (compartilhados com a pré-carga em services/profilePrefetch)
const getLocalCache = getProfileCache;
const setLocalCache = setProfileCache;

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { username: usernameParam, postId: postIdParam } = useParams<{ username: string; postId?: string }>();
  const { user, logout } = useAuth();
  const { accentColor, setAccentColor, isDarkMode } = useTheme();
  const { profile, books: contextBooks, friends: contextFriends, refreshProfile, refreshBooks } = useData();

  const navigate = useNavigate();

  // ─── Estado central ───────────────────────────────────────────
  const [viewedUsername, setViewedUsername]   = useState<string | null>(null);
  const [viewedUserId,   setViewedUserId]     = useState<string | null>(null);

  // Cache key inicial
  const initialCacheKey = usernameParam || profile?.username || user?.id || 'me';
  // Em /profile (sem @), o cache "meta_me" vem da pré-carga feita ao abrir o app
  const initialCachedMeta = getLocalCache(`meta_${initialCacheKey}`) || (!usernameParam ? getLocalCache('meta_me') : null);

  // Dados do perfil visualizado (inicia com cache se disponível)
  const [profileMeta, setProfileMeta] = useState(initialCachedMeta || {
    id: '',
    displayName: '',
    username: '',
    bio: '',
    avatarUrl: '',
    avatarConfig: null as AvatarConfig | null,
    planType: 'free',
    displaySettings: null as any
  });

  // Conteúdo das abas (inicia com cache se disponível)
  const initialUserId = initialCachedMeta?.id || user?.id;
  const [posts,      setPosts]      = useState<any[]>(getLocalCache(`posts_${initialUserId}`) || []);
  const [books,      setBooks]      = useState<any[]>(getLocalCache(`books_${initialUserId}`) || []);
  const [friends,    setFriends]    = useState<any[]>(getLocalCache(`friends_${initialUserId}`) || []);
  const [savedPosts, setSavedPosts] = useState<any[]>(getLocalCache(`saved_${initialUserId}`) || []);

  // Loading individual por secção
  const [loadingMeta,    setLoadingMeta]    = useState(!initialCachedMeta);
  const [loadingContent, setLoadingContent] = useState(!initialCachedMeta);
  const [isSaving,       setIsSaving]       = useState(false);

  // UI
  const [activeTab, setActiveTab] = useState<'posts' | 'estante' | 'amigos' | 'salvos'>('posts');
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [selectedBook, setSelectedBook] = useState<any | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<{ isOpen: boolean; message: string; isDuplicate?: boolean; bookId?: string }>({
    isOpen: false,
    message: '',
    isDuplicate: false,
    bookId: ''
  });

  // When URL has a postId param, open that post modal once posts are loaded
  useEffect(() => {
    if (!postIdParam || posts.length === 0) return;
    const target = posts.find((p: any) => String(p.id) === String(postIdParam));
    if (target) setSelectedPost(target);
  }, [postIdParam, posts]);

  // ─── PASSO 1: Determinar identidade e buscar metadados ───
  useEffect(() => {
    let isMounted = true;

    const fetchIdentityAndMeta = async () => {
      // Com dados em cache a página já aparece pronta; o spinner só surge quando não há nada para mostrar
      const cachedForRoute = getLocalCache(`meta_${usernameParam || 'me'}`);
      if (!cachedForRoute) setLoadingMeta(true);
      try {
        if (!usernameParam) {
          if (!user?.id) return;
          // Atalho: o @ já conhecido pela pré-carga leva direto ao perfil, sem esperar o servidor
          const cachedMe = getLocalCache('meta_me');
          if (cachedMe?.id === user.id && cachedMe?.username && (window.location.pathname === '/profile' || window.location.pathname === '/profile/')) {
            navigate(`/profile/${cachedMe.username}`, { replace: true });
            prefetchOwnProfile(user);
            return;
          }
          const results = await dbManager.execute({
            action: 'select',
            table: 'profiles',
            filters: { id: user.id },
            user_id: user.id,
          });
          if (!isMounted) return;

          const data = Array.isArray(results) ? results : (results?.data || []);
          const p = data[0] ?? null;
          const handle = p?.profiles?.username || p?.username;
          
          if (isMounted && (window.location.pathname === '/profile' || window.location.pathname === '/profile/')) {
            if (handle) {
              navigate(`/profile/${handle}`, { replace: true });
            } else if (profile?.username) {
              navigate(`/profile/${profile.username}`, { replace: true });
            }
          }
          return;
        }

        let results = await dbManager.execute({
          action: 'select_public',
          table: 'profiles',
          filters: { username: usernameParam },
        });
        if (!isMounted) return;

        let data = Array.isArray(results) ? results : (results?.data || []);
        let p = data[0] ?? null;

        if (!p) {
          const historyResults = await dbManager.execute({
            action: 'select_public',
            table: 'profiles',
            filters: { LIKE_OLD_USERNAMES: `"${usernameParam}"` },
          });
          if (!isMounted) return;

          const historyData = Array.isArray(historyResults) ? historyResults : (historyResults?.data || []);
          p = historyData[0] ?? null;

          if (p) {
            const pFields = p.profiles || {};
            const currentHandle = pFields.username || p.username;
            if (currentHandle && currentHandle !== usernameParam && isMounted && window.location.pathname.startsWith('/profile')) {
              navigate(`/profile/${currentHandle}`, { replace: true });
              return;
            }
          }
        }

        if (p && isMounted) {
          const isMe = user?.id && p.id === user.id;

          setViewedUserId(p.id);
          setViewedUsername(usernameParam);
          const newMeta = buildProfileMeta(p, user, usernameParam);
          setProfileMeta(newMeta);
          setLocalCache(`meta_${usernameParam || newMeta.username || newMeta.id}`, newMeta);
          if (isMe) setLocalCache(`meta_me`, newMeta);
        } else if (isMounted) {
          // Perfil não encontrado: não há abas para buscar, então o "carregando" do conteúdo também termina
          setLoadingContent(false);
        }
      } catch (err) {
        console.error('[ProfilePage] Erro ao buscar perfil:', err);
        if (isMounted) setLoadingContent(false);
      } finally {
        if (isMounted) setLoadingMeta(false);
      }
    };

    fetchIdentityAndMeta();

    return () => {
      isMounted = false;
    };
  }, [usernameParam, user?.id, navigate, profile?.username]);

  const isOwnProfile = (!usernameParam) || (user?.id && profileMeta.id === user.id) || (profile?.username && (usernameParam === profile?.username || profileMeta.username === profile?.username));

  // Safeguard: Ensure visitors never have activeTab = 'salvos'
  useEffect(() => {
    if (!isOwnProfile && activeTab === 'salvos') {
      setActiveTab('posts');
    }
  }, [isOwnProfile, activeTab]);

  // ─── PASSO 3: Buscar conteúdo das abas no D1 ─────────────────
  useEffect(() => {
    if (!viewedUserId) return;

    const fetchContent = async () => {
      try {
        // Mesmas consultas de antes, agora compartilhadas com a pré-carga (também grava o cache)
        const content = await fetchProfileContent(viewedUserId, !!isOwnProfile);
        setPosts(content.posts);
        setBooks(content.books);
        setFriends(content.friends);
        if (isOwnProfile) setSavedPosts(content.saved);
      } catch (err) {
        console.error('[ProfilePage] Erro ao buscar conteúdo D1:', err);
      } finally {
        setLoadingContent(false);
      }
    };

    fetchContent();
  }, [viewedUserId, isOwnProfile]);

  const handleApplyColor = (color: string) => {
    setAccentColor(color);
    setIsColorPickerOpen(false);
  };

  const handleBookAction = (book: any) => {
    setSelectedBook(book);
  };

  const handleImport = async (book: any) => {
    if (!user || isImporting) return;
    setIsImporting(true);
    try {
      const bookId = book.id || book.custom_id;
      const results = await dbManager.from('books', user.id).select({ id: bookId });
      const existing = Array.isArray(results) ? results : (results?.data || []);
      
      if (existing.length > 0) {
        setImportStatus({
          isOpen: true,
          message: "Você já possui esse livro na sua estante",
          isDuplicate: true
        });
        setIsImporting(false);
        return;
      }

      await dbManager.from('books', user.id).upsert({
        id: String(bookId),
        custom_id: String(bookId),
        OR_ID_CUSTOM_ID: String(bookId),
        title: book.title,
        author: book.author || 'Autor Desconhecido',
        file_url: book.file_url || `${R2_HTML_BASE}${bookId}.html`,
        cover_url: book.cover_url || `${R2_COVER_BASE}${bookId}.jpg`,
        last_page: 1,
        user_id: user.id,
        file_type: book.file_type || 'html',
        language: book.language || 'und',
        category: book.category || 'Salvo do Perfil',
        display_order: Date.now()
      });

      await refreshProfile();
      await refreshBooks();

      setImportStatus({
        isOpen: true,
        message: "Livro salvo na sua estante!",
        isDuplicate: false,
        bookId: String(bookId)
      });
      setSelectedBook(null);
    } catch (err) {
      console.error('[ProfilePage] Erro ao importar livro:', err);
    } finally {
      setIsImporting(false);
    }
  };

  if (!user) return null;

  if (!usernameParam && !viewedUsername && loadingMeta) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-primary w-10 h-10" />
      </div>
    );
  }

  const ds = profileMeta.displaySettings;

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-8 pb-20 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <AppBackButton fallbackTo="/" />
          <PageTourButton tourKey="profile" />
        </div>

        {/* ── HEADER (design 2.0): cartão com brilho na cor de destaque, avatar com anel orbital e ações em pílulas ── */}
        <motion.div
          data-tour="profile-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-surface rounded-[32px] p-7 md:p-10 border border-black/[0.06] dark:border-white/[0.06] overflow-hidden"
        >
          {/* Brilho suave no canto (cor de destaque do usuário) */}
          <div aria-hidden className="pointer-events-none absolute -top-32 -right-24 w-80 h-80 rounded-full bg-primary/[0.14] blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-28 w-80 h-80 rounded-full bg-tint-lavender/40 dark:bg-tintd-lavender/10 blur-3xl" />

          {/* Configurações: orbe no canto */}
          {isOwnProfile && (
            <Link
              to="/settings"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-primary/[0.05] hover:bg-primary hover:text-slate-900 text-ink flex items-center justify-center transition-colors z-10"
              title={t('profile.settings.title', 'Configurações')}
              aria-label={t('profile.settings.title', 'Configurações')}
            >
              <AnimatedIcon motion="spin"><Settings size={18} strokeWidth={1.75} /></AnimatedIcon>
            </Link>
          )}

          <div className="relative flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
            {/* Avatar com anel orbital girando devagar */}
            <div className="relative w-36 h-36 md:w-40 md:h-40 shrink-0">
              <motion.div
                aria-hidden
                className="absolute -inset-2 rounded-full"
                style={{ background: 'conic-gradient(from 0deg, rgba(var(--primary-rgb), 0.9), transparent 35%, rgba(var(--primary-rgb), 0.35) 60%, transparent 85%, rgba(var(--primary-rgb), 0.9))' }}
                animate={{ rotate: 360 }}
                transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
              />
              <div className="absolute -inset-[5px] rounded-full bg-surface" />
              <div className="relative w-full h-full rounded-full overflow-hidden bg-tint-stone">
                {loadingMeta ? (
                  <div className="w-full h-full flex items-center justify-center"><Loader2 className="animate-spin text-muted" /></div>
                ) : profileMeta.avatarConfig ? (
                  <AvatarRenderer config={profileMeta.avatarConfig} seed={profileMeta.username} />
                ) : (
                  <img
                    src={profileMeta.avatarUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${profileMeta.displayName || 'U'}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                    className="w-full h-full object-cover"
                    alt="avatar"
                  />
                )}
              </div>
              {profileMeta.planType === 'pro' && (
                <span className="absolute bottom-1 right-1 h-7 px-2.5 rounded-full bg-ink text-surface text-[10px] font-mono tracking-[0.14em] flex items-center gap-1 ring-4 ring-surface">
                  <Sparkles size={10} /> PRO
                </span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 w-full space-y-5">
              <div>
                <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">{isOwnProfile ? 'seu perfil' : 'perfil'}</span>
                <h2 data-tour="page-title" className="font-display text-4xl md:text-5xl font-medium text-ink tracking-[-0.04em] leading-[1.05] mt-1 break-words">
                  {loadingMeta ? <Loader2 className="inline animate-spin text-muted w-8 h-8" /> : (profileMeta.displayName || t('common.user'))}<span className="text-primary">.</span>
                </h2>
                <p className="font-mono text-sm text-muted mt-2">@{loadingMeta ? '...' : profileMeta.username}</p>

                {/* Idiomas/níveis e conquista em destaque */}
                {(ds?.showLevel || (ds?.showAchievements && ds?.selectedAchievementName)) && (
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-4">
                    {ds?.showLevel && (Array.isArray(ds?.selectedLevelLangs) && ds.selectedLevelLangs.length > 0 ? (
                      ds.selectedLevelLangs.map((langCode: string) => {
                        const flag = ISO_FLAG_MAP[langCode.toLowerCase()] || '🌐';
                        const cefr = ds?.testedLevels?.[langCode.toLowerCase()] || 'B1';
                        return (
                          <span key={langCode} className="h-8 px-3 rounded-full bg-tint-sage text-ink text-xs flex items-center gap-1.5">
                            <span className="text-sm">{flag}</span>
                            <span className="font-mono uppercase tracking-[0.08em]">{langCode} {cefr}</span>
                          </span>
                        );
                      })
                    ) : (
                      <span className="h-8 px-3 rounded-full bg-tint-sage text-ink text-xs flex items-center gap-1.5">
                        <span className="text-sm">{ISO_FLAG_MAP[ds?.selectedLevelLang || 'fr'] || '🇫🇷'}</span>
                        <span className="font-mono uppercase tracking-[0.08em]">{ds?.selectedLevelLang || 'fr'} {ds?.selectedLevelCefr || 'B1'}</span>
                      </span>
                    ))}
                    {ds?.showAchievements && ds?.selectedAchievementName && (
                      <span className="h-8 px-3 rounded-full bg-tint-butter text-ink text-xs flex items-center gap-1.5">
                        <Trophy size={13} strokeWidth={1.75} />
                        <span>{ds.selectedAchievementName}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <p className="text-[15px] text-ink/70 leading-relaxed md:border-l-2 md:border-primary/80 md:pl-4">
                {loadingMeta ? '...' : (profileMeta.bio || 'Onde livros ensinam línguas')}
              </p>

              {/* Contadores (clicáveis) */}
              <div data-tour="profile-counters" className="grid grid-cols-3 gap-2 max-w-md mx-auto md:mx-0">
                {[
                  { n: posts.length, label: t('profile.stats.posts', 'POSTS'), onClick: () => setActiveTab('posts') as any, to: null as string | null },
                  { n: friends.length, label: t('profile.stats.amigos', 'AMIGOS'), onClick: null as any, to: '/friends' },
                  { n: books.length, label: t('profile.stats.livros', 'LIVROS'), onClick: null as any, to: '/library' },
                ].map((c, i) => {
                  const inner = (
                    <>
                      <div className="font-display text-3xl font-medium text-ink tracking-[-0.04em] tabular-nums group-hover:text-primary transition-colors">
                        {loadingContent && c.n === 0 ? <Loader2 className="inline animate-spin w-5 h-5 text-muted" /> : c.n}
                      </div>
                      <div className="text-[10px] uppercase font-mono tracking-[0.14em] text-muted mt-0.5">{c.label}</div>
                    </>
                  );
                  const cls = 'group rounded-[20px] bg-ink/[0.03] dark:bg-white/[0.03] hover:bg-ink/[0.06] dark:hover:bg-white/[0.06] px-4 py-3 text-center md:text-left select-none cursor-pointer transition-colors';
                  return (
                    <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.15 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}>
                      {c.to ? <Link to={c.to} className={`block ${cls}`}>{inner}</Link> : <div onClick={c.onClick} className={cls}>{inner}</div>}
                    </motion.div>
                  );
                })}
              </div>

              {/* Ações */}
              {loadingMeta ? (
                <div className="flex items-center justify-center md:justify-start h-11"><Loader2 className="animate-spin text-muted w-5 h-5" /></div>
              ) : isOwnProfile ? (
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                  {[
                    { to: '/achievements', label: 'Conquistas', Icon: Trophy, motion: 'swing' as const, tint: 'bg-tint-butter' },
                    { to: '/reports', label: 'Estatísticas', Icon: BarChart2, motion: 'bounce' as const, tint: 'bg-tint-lavender' },
                    { to: '/level-test', label: 'Meu nível', Icon: BrainCircuit, motion: 'pulse' as const, tint: 'bg-tint-sage' },
                  ].map(a => (
                    <Link
                      key={a.to}
                      to={a.to}
                      title={a.label}
                      className={`group h-11 pl-1.5 pr-4 rounded-full ${a.tint} text-ink text-sm flex items-center gap-2.5 hover:-translate-y-0.5 active:scale-95 transition-transform`}
                    >
                      <span className="w-8 h-8 rounded-full bg-surface/70 dark:bg-black/20 flex items-center justify-center">
                        <AnimatedIcon motion={a.motion}><a.Icon size={15} strokeWidth={1.75} /></AnimatedIcon>
                      </span>
                      {a.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex justify-center md:justify-start">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/chat', { state: { friendId: profileMeta.id } }); }}
                    className="group h-11 pl-1.5 pr-5 rounded-full bg-primary text-slate-900 text-sm flex items-center gap-2.5 hover:-translate-y-0.5 active:scale-95 transition-transform cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-full bg-surface/15 flex items-center justify-center">
                      <AnimatedIcon motion="wobble"><MessageCircle size={15} strokeWidth={1.75} /></AnimatedIcon>
                    </span>
                    {t('common.chat', 'Chat')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* ── TABS (Amigos nunca vêm 'salvos') ── */}

        {/* ── TABS (Amigos nunca vêm 'salvos') ── */}
        <div data-tour="profile-tabs" className="flex items-center gap-1 p-1 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-full w-fit max-w-full overflow-x-auto mx-auto mb-8">
          {(['posts', 'estante', 'amigos', ...(isOwnProfile ? ['salvos'] : [])] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`h-10 px-5 rounded-full text-sm whitespace-nowrap transition-colors duration-300 ${activeTab === tab ? 'bg-ink text-surface' : 'text-muted hover:text-ink'}`}
            >
              {tab === 'posts'        && <MessageCircle size={14} className="inline mr-2" />}
              {tab === 'estante'      && <Library       size={14} className="inline mr-2" />}
              {tab === 'amigos'       && <Users          size={14} className="inline mr-2" />}
              {tab === 'salvos'       && <Bookmark       size={14} className="inline mr-2" />}
              {t(`profile.tabs.${tab === 'salvos' ? 'saved' : tab}`)}
            </button>
          ))}
        </div>

        {/* ── CONTEÚDO ── */}
        <div data-tour="profile-content" className="bg-surface rounded-[32px] p-6 md:p-10 border border-black/[0.06] dark:border-white/[0.06] min-h-[500px]">

          {/* Posts & Salvos */}
          {(activeTab === 'posts' || (activeTab === 'salvos' && isOwnProfile)) && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {loadingContent ? (
                <Loader2 className="animate-spin col-span-full mx-auto mt-20 text-primary" />
              ) : (activeTab === 'posts' ? posts : savedPosts).length > 0 ? (
                (activeTab === 'posts' ? posts : savedPosts).map(post => {
                  const parsed = extractPostStyle(post.content);
                  const firstImg = post.media_url || Object.values(parsed.style?.imgs || {})[0];
                  return (
                    <div
                      key={post.id}
                      onClick={() => {
                        const targetUsername = viewedUsername || usernameParam;
                        if (targetUsername) {
                          navigate(`/profile/${targetUsername}/${post.id}`, { replace: false });
                        }
                        setSelectedPost(post);
                      }}
                      className="aspect-[9/16] rounded-3xl bg-slate-50 dark:bg-slate-800/40 overflow-hidden cursor-pointer hover:scale-[1.03] transition-all relative border border-black/[0.06] dark:border-white/[0.06] group"
                    >
                      {firstImg
                        ? <img src={firstImg as string} className="w-full h-full object-cover" alt="" />
                        : <div className="p-6 flex items-center justify-center h-full text-center text-xs font-bold" style={{ backgroundColor: parsed.style?.bg || 'transparent' }}>"{parsed.text.slice(0, 40)}..."</div>
                      }
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Sparkles className="text-white scale-150" />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-32 text-center text-slate-300 font-medium text-sm">{t('profile.empty')}</div>
              )}
            </div>
          )}

          {/* Estante */}
          {activeTab === 'estante' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {loadingContent ? (
                <Loader2 className="animate-spin col-span-full mx-auto mt-20 text-primary" />
              ) : books.length > 0 ? (
                books.map(book => (
                  <div
                    key={book.id || book.custom_id}
                    onClick={() => handleBookAction(book)}
                    className="group relative aspect-[3/4] rounded-3xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-50 dark:border-white/[0.06] cursor-pointer hover:-translate-y-2 transition-all"
                  >
                    <BookCover book={book} />
                    
                    <div className="absolute top-4 right-4 bg-black/20 backdrop-blur-md p-2 rounded-xl text-white opacity-50 group-hover:opacity-100 transition-all z-10">
                      <Eye size={14} />
                    </div>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent flex flex-col justify-end p-5 pl-[10%] pb-6">
                      <h4 className="text-white font-normal text-[10px] line-clamp-2 uppercase font-mono tracking-[0.14em] leading-tight drop-shadow-md">
                        {book.title}
                      </h4>
                      <p className="text-white/60 font-normal text-[8px] uppercase font-mono tracking-[0.14em] mt-1 line-clamp-1 drop-shadow-md">
                        {book.author || 'Autor Desconhecido'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-32 text-center text-slate-300 font-medium text-sm">{t('profile.noBooks')}</div>
              )}
            </div>
          )}

          {/* Amigos */}
          {activeTab === 'amigos' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {loadingContent ? (
                <Loader2 className="animate-spin col-span-full mx-auto mt-20 text-primary" />
              ) : friends.length > 0 ? (
                friends.map(f => (
                  <div
                    key={f.id}
                    onClick={async () => {
                      const { profileService } = await import('../services/profileService');
                      await profileService.refreshTargetProfile(f.username);
                      navigate(`/profile/${f.username}`);
                    }}
                    className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-[24px] flex items-center gap-6 cursor-pointer border border-transparent hover:border-primary/20 hover:bg-white dark:hover:bg-slate-800 transition-all group"
                  >
                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-700 group-hover:scale-110 transition-transform">
                      <img src={f.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${f.display_name}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`} className="w-full h-full object-cover" alt={f.display_name} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm dark:text-ink group-hover:text-primary transition-colors">{f.display_name}</h4>
                      <p className="text-[10px] text-slate-400 font-semibold">@{f.username}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-32 text-center text-slate-300 font-medium text-sm">{t('profile.emptyFriends')}</div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Modals */}
      <ColorPickerModal isOpen={isColorPickerOpen} onClose={() => setIsColorPickerOpen(false)} currentColor={accentColor} onApply={handleApplyColor} />
      <AvatarCreatorModal
        isOpen={isAvatarPickerOpen}
        onClose={() => setIsAvatarPickerOpen(false)}
        onSave={(cfg) => { setProfileMeta(p => ({ ...p, avatarConfig: cfg })); setIsAvatarPickerOpen(false); }}
        initialConfig={profileMeta.avatarConfig}
      />
      <SuccessModal isOpen={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)} message={successMessage} />
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={() => {
            setSelectedPost(null);
            const targetUsername = viewedUsername || usernameParam;
            if (targetUsername) {
              navigate(`/profile/${targetUsername}`, { replace: true });
            }
          }}
          onUpdate={async () => {
            if (viewedUserId) {
              const res = await socialService.getUserPosts(viewedUserId);
              setPosts(Array.isArray(res) ? res : (res?.data || []));
            }
            refreshProfile();
          }}
        />
      )}

      {selectedBook && (
        <BookDetailsModal
          book={{
            id: selectedBook.id || selectedBook.custom_id,
            title: selectedBook.title,
            author: selectedBook.author || 'Autor Desconhecido',
            language: selectedBook.language || 'und',
            cover: selectedBook.cover_url || '',
            file: selectedBook.file_url || '',
            format: selectedBook.file_type || 'html'
          }}
          onClose={() => setSelectedBook(null)}
          onImport={handleImport}
          onPreview={(b) => navigate(`/reader/${b.id}`)}
          isImporting={isImporting}
          languageFlags={ISO_FLAG_MAP}
          renderCover={(b) => <BookCover book={{ ...selectedBook, ...b }} />}
          primaryLabel={t('library.saveToShelf', 'Salvar na Estante')}
          secondaryLabel={t('profile.readNow', 'Ler agora')}
          secondaryIcon={<BookOpen size={20} />}
        />
      )}

      <ImportSuccessModal 
        isOpen={importStatus.isOpen} 
        onClose={() => setImportStatus(s => ({ ...s, isOpen: false }))}
        message={importStatus.message}
        isDuplicate={importStatus.isDuplicate}
        onReadNow={importStatus.bookId ? () => navigate(`/reader/${importStatus.bookId}`) : undefined}
      />
    </>
  );
};

export default ProfilePage;
